import { DayPicker } from '@natera/material/lib/date'
import { isValid, parse } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Popover, PopoverAnchor, PopoverContent } from '~/shared/ui'
import {
  DATE_FORMAT,
  MASK_TEMPLATE,
  dateToDigits,
  dateToDisplay,
  dateToStoredString,
  digitsToMasked,
  maskedCursorPosition,
  parseStoredDate,
} from '../../../lib'

interface ServiceDateCellProps {
  value: string
  onValueChange: (value: string) => void
  onBlur?: () => void
}

const inputClass =
  'text-text-primary w-full border-none bg-transparent px-0 py-1 text-[15px] leading-5 tracking-[0.25px] outline-none'

export function ServiceDateCell({
  value,
  onValueChange,
  onBlur: onBlurProp,
}: ServiceDateCellProps) {
  const storedDate = parseStoredDate(value)

  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)
  const [digits, setDigits] = useState(() => (storedDate ? dateToDigits(storedDate) : ''))
  const inputRef = useRef<HTMLInputElement>(null)
  const cursorRef = useRef<number | null>(null)
  const selectingDayRef = useRef(false)

  // Sync digits when value changes externally
  useEffect(() => {
    const d = parseStoredDate(value)
    setDigits(d ? dateToDigits(d) : '')
  }, [value])

  // Place cursor after render
  useEffect(() => {
    if (cursorRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorRef.current, cursorRef.current)
      cursorRef.current = null
    }
  })

  const tryParseAndUpdate = useCallback(
    (rawDigits: string) => {
      if (rawDigits.length === 8) {
        const masked = digitsToMasked(rawDigits)
        const parsed = parse(masked, DATE_FORMAT, new Date())
        if (isValid(parsed)) {
          onValueChange(dateToStoredString(parsed))
          return
        }
      }
      if (rawDigits.length === 0) {
        onValueChange('')
      }
    },
    [onValueChange],
  )

  const handleDaySelect = useCallback(
    (newDate: Date | undefined) => {
      if (!newDate) return
      onValueChange(dateToStoredString(newDate))
      setDigits(dateToDigits(newDate))
      setOpen(false)
      onBlurProp?.()
      inputRef.current?.focus()
    },
    [onValueChange, onBlurProp],
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const incoming = e.target.value.replace(/\D/g, '')
      const clamped = incoming.slice(0, 8)
      setDigits(clamped)
      cursorRef.current = maskedCursorPosition(clamped.length)
      tryParseAndUpdate(clamped)
    },
    [tryParseAndUpdate],
  )

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = e.currentTarget

      if (e.key === 'Backspace') {
        e.preventDefault()
        const selStart = input.selectionStart ?? digits.length
        const selEnd = input.selectionEnd ?? selStart

        if (selStart !== selEnd) {
          let firstDigit = 0
          let lastDigit = 0
          let count = 0
          for (let i = 0; i < MASK_TEMPLATE.length && count <= digits.length; i++) {
            if (MASK_TEMPLATE[i] === '/') continue
            if (i < selStart) firstDigit = count + 1
            if (i < selEnd) lastDigit = count + 1
            count++
          }
          const next = digits.slice(0, firstDigit) + digits.slice(lastDigit)
          setDigits(next)
          cursorRef.current = maskedCursorPosition(firstDigit)
          tryParseAndUpdate(next)
        } else if (digits.length > 0) {
          let digitIndex = 0
          for (let i = 0; i < MASK_TEMPLATE.length && i < selStart; i++) {
            if (MASK_TEMPLATE[i] !== '/') digitIndex++
          }
          const delIdx = Math.max(0, digitIndex - 1)
          if (delIdx < digits.length) {
            const next = digits.slice(0, delIdx) + digits.slice(delIdx + 1)
            setDigits(next)
            cursorRef.current = maskedCursorPosition(delIdx)
            tryParseAndUpdate(next)
          }
        }
        return
      }

      if (e.key === 'Delete') {
        e.preventDefault()
        const selStart = input.selectionStart ?? 0
        const selEnd = input.selectionEnd ?? selStart

        if (selStart !== selEnd) {
          let firstDigit = 0
          let lastDigit = 0
          let count = 0
          for (let i = 0; i < MASK_TEMPLATE.length && count <= digits.length; i++) {
            if (MASK_TEMPLATE[i] === '/') continue
            if (i < selStart) firstDigit = count + 1
            if (i < selEnd) lastDigit = count + 1
            count++
          }
          const next = digits.slice(0, firstDigit) + digits.slice(lastDigit)
          setDigits(next)
          cursorRef.current = maskedCursorPosition(firstDigit)
          tryParseAndUpdate(next)
        } else {
          let digitIndex = 0
          for (let i = 0; i < MASK_TEMPLATE.length && i < selStart; i++) {
            if (MASK_TEMPLATE[i] !== '/') digitIndex++
          }
          if (digitIndex < digits.length) {
            const next = digits.slice(0, digitIndex) + digits.slice(digitIndex + 1)
            setDigits(next)
            cursorRef.current = maskedCursorPosition(digitIndex)
            tryParseAndUpdate(next)
          }
        }
        return
      }

      if (e.key === 'Enter') {
        if (digits.length === 8) {
          const masked = digitsToMasked(digits)
          const parsed = parse(masked, DATE_FORMAT, new Date())
          if (isValid(parsed)) {
            onValueChange(dateToStoredString(parsed))
            setOpen(false)
          }
        }
        return
      }

      if (e.key === 'Escape') {
        setOpen(false)
      }
    },
    [digits, onValueChange, tryParseAndUpdate],
  )

  const handleInputBlur = useCallback(() => {
    // Skip blur when a day was just selected — handleDaySelect handles value + history
    if (selectingDayRef.current) {
      selectingDayRef.current = false
      return
    }

    setFocused(false)
    if (digits.length === 0) {
      if (value !== '') {
        onValueChange('')
      }
      onBlurProp?.()
      return
    }
    if (digits.length === 8) {
      const masked = digitsToMasked(digits)
      const parsed = parse(masked, DATE_FORMAT, new Date())
      if (isValid(parsed)) {
        const stored = dateToStoredString(parsed)
        if (stored !== value) {
          onValueChange(stored)
        }
        onBlurProp?.()
        return
      }
    }
    // Incomplete or invalid — revert
    setDigits(storedDate ? dateToDigits(storedDate) : '')
  }, [digits, storedDate, value, onValueChange, onBlurProp])

  const handleFocus = useCallback(() => {
    setFocused(true)
    setOpen(true)
    const pos = maskedCursorPosition(digits.length)
    requestAnimationFrame(() => {
      inputRef.current?.setSelectionRange(pos, pos)
    })
  }, [digits.length])

  // Determine display value
  let displayValue: string
  if (focused) {
    displayValue = digits.length > 0 ? digitsToMasked(digits) : MASK_TEMPLATE
  } else {
    displayValue = storedDate ? dateToDisplay(storedDate) : ''
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverAnchor asChild>
        <input
          ref={inputRef}
          className={inputClass}
          value={displayValue}
          placeholder=""
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onFocus={handleFocus}
          onBlur={handleInputBlur}
          onClick={() => setOpen(true)}
        />
      </PopoverAnchor>

      <PopoverContent
        className="p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onMouseDown={() => {
          selectingDayRef.current = true
        }}
        alignOffset={-8}
        sideOffset={9}
      >
        <DayPicker
          date={storedDate}
          onDateChange={handleDaySelect}
          defaultMonthYear={storedDate}
          materialArrowIcon="chevron_right"
          materialDropdownIcon="arrow_drop_down"
        />
      </PopoverContent>
    </Popover>
  )
}
